import axios from "axios";
import { attachAuthInterceptors } from "./authInterceptors";

const getCurrentBaseUrl = () => {
  // 0) Runtime override for flexibility (no .env needed):
  // - Accept ?api=http://<host>:<port> in the URL and persist to localStorage
  // - Or read an existing localStorage override key: 'API_BASE_URL_OVERRIDE'
  try {
    const { href } = window.location;
    const url = new URL(href);
    const overrideFromQuery = url.searchParams.get("api");
    if (overrideFromQuery && overrideFromQuery.trim().length > 0) {
      const trimmed = overrideFromQuery.trim();
      if (trimmed.toLowerCase() === "clear") {
        localStorage.removeItem("API_BASE_URL_OVERRIDE");
      } else {
        localStorage.setItem("API_BASE_URL_OVERRIDE", trimmed);
      }
      // Clean the URL to avoid leaking the param
      url.searchParams.delete("api");
      history.replaceState(null, "", url.toString());
    }
  } catch {}

  try {
    const lsOverride = localStorage.getItem("API_BASE_URL_OVERRIDE");
    if (lsOverride && lsOverride.trim().length > 0) {
      return lsOverride.trim();
    }
  } catch {}

  // 1) Explicit override via env for all modes (dev/preview/prod)
  const envRaw = typeof import.meta !== "undefined" && (import.meta as unknown as { env?: Record<string, string> }).env;
  const envBase = envRaw && typeof envRaw.VITE_API_BASE_URL === "string" ? envRaw.VITE_API_BASE_URL : undefined;
  if (envBase && envBase.trim().length > 0) return envBase.trim();

  // 2) Localhost/LAN detection: if running locally (including vite dev/preview), hit local API on :4000
  try {
    const { protocol, hostname, port } = window.location;
    const isLocal =
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname === "0.0.0.0" ||
      hostname.startsWith("192.168.") ||
      hostname.startsWith("10.") ||
      hostname.endsWith(".local");
    const hasDevPort = !!port && port !== "80" && port !== "443"; // covers vite dev (5173) and preview (4173)
    if (!import.meta.env.SSR && (isLocal || hasDevPort)) {
      return `${protocol}//${hostname}:4000`;
    }
  } catch {}

  // 3) Production: Auto-detect API domain based on current hostname
  try {
    const hostname = window.location.hostname;
    if (hostname.includes("garagegymzaandam.nl")) {
      return "https://api.garagegymzaandam.nl";
    }
  } catch {}

  // 4) Default to production API
  return "https://api.nwpt.xyz";
};

// Call function at runtime, not at module load time
let cachedBaseURL: string | null = null;
const getBaseURL = () => {
  if (!cachedBaseURL) {
    cachedBaseURL = getCurrentBaseUrl();
    console.log("API base URL:", cachedBaseURL);
  }
  return cachedBaseURL;
};

const axiosInstance = axios.create({
  get baseURL() {
    return getBaseURL();
  },
  withCredentials: true, // Enable cookies
});

const loginAxiosInstance = axios.create({
  get baseURL() {
    return getBaseURL();
  },
  withCredentials: true, // Enable cookies
});

// Response interceptor for loginAxiosInstance to unwrap standardized API response
loginAxiosInstance.interceptors.response.use(
  (response) => {
    // Unwrap standardized API response format: { success: true, data: T }
    if (response.data && typeof response.data === 'object' && 'success' in response.data && 'data' in response.data) {
      response.data = response.data.data;
    }
    return response;
  },
  (error) => Promise.reject(error)
);

// Shared auth/CSRF behavior (CSRF header, token refresh queue, CSRF retry,
// envelope unwrap, login redirect on auth failure, clears localStorage "user").
attachAuthInterceptors(axiosInstance, {
  getBaseUrl: getBaseURL,
  onRequest: (config) => {
    // Add View As User header if present
    if (typeof localStorage !== "undefined") {
      const viewAsId = localStorage.getItem("viewAsUserId");
      if (viewAsId) {
        config.headers["x-view-as-user"] = viewAsId;
      }
    }
  },
});

export { axiosInstance, loginAxiosInstance };
